import * as jsCookie from 'js-cookie';
import { ADOBE as config } from './constants';
import logger from './logger';
import sendRequest from './sendRequest';

export type Mcid = string | null;

export type AdobeData = {
    mcid: Mcid;
    sdid?: string;
}

export type AdobeDataResponse = {
    data: {
        d_mid: string;
    }
}
export type CheggMcidResponse = {
    data: {
        mcid: string;
    }
}
export const getCacheBuster = (url: string): string => {
    const rando = Math.floor(Math.random() * 1000000000);
    const qStringConnector = url.indexOf('?') > -1 ? '&' : '?';
    return `${qStringConnector}${config.cacheBuster}=${rando}`;
};

export const fetchFromRemote = async (url: string): Promise<Mcid> => {
    let mcid: Mcid = null;
    let sendError: Error | undefined;
    try {
        const cacheBuster = getCacheBuster(url);
        const response: AdobeDataResponse | Error = await sendRequest({ url: `${url}${cacheBuster}`, options: { withCredentials: true } });
        if (response instanceof Error) throw response;
        mcid = extractMcid(response);
    } catch (e) {
        sendError = e
    }
    if (!mcid) {
        const error = sendError || new Error;
        error.message = `Failed to gather MCID from ${url}. ${error.message}`;
        logger.error(error);
    }
    return mcid;
};

export const extractMcid = ({ data }: AdobeDataResponse | CheggMcidResponse): Mcid => {
    let mcid: Mcid = null;
    if ('d_mid' in data) {
        mcid = data.d_mid
    } else if ('mcid' in data) {
        mcid = data.mcid;
    }
    return mcid;
};

let externalResolve: (value: AdobeData) => void;
let adobeDataPromise = new Promise((resolve) => {
    externalResolve = (value: AdobeData) => {
        resolve(value)
    };
});

// For testing:
export const reset = () => {
    adobeDataPromise = new Promise((resolve) => {
        externalResolve = (value: AdobeData) => {
            resolve(value)
        }
    });
};

export const init = async (): Promise<AdobeData> => {

    // Attempt to get MCID from cookie:
    let mcid: Mcid = jsCookie.get(config.cookieName) || null;

    if (!mcid) {
        // MCID was not yet cookied.  Attempt to get MCID from Adobe endpoint:
        mcid = await fetchFromRemote(config.url);
    }

    if (!mcid) {
        // Unable to get cookie from Adobe.  Attempt to get MCID from Chegg endpoint:
        mcid = await fetchFromRemote(config.cheggMCIDUrl);
    }

    // Only set the cookie if we received a valid MCID
    if (mcid) {
        jsCookie.set(config.cookieName, mcid);
    }

    externalResolve({ mcid });
    return { mcid };
}

// Start the acquisition of the data at bootstrapping, and expose the resulting promise:
export type GetAdobeDataFn = () => Promise<AdobeData>;
export const getAdobeData = (): Promise<AdobeData> => adobeDataPromise as Promise<AdobeData>;
